import { lazy, type ComponentType } from "react";
import { importWithRecovery } from "../lib/chunkLoadRecovery";
import LazyPageErrorBoundary from "./LazyPageErrorBoundary";
import { trackPageLoad } from "./pageLoadStatus";
import type { PagePlacement } from "./pagePlacement";

/**
 * Страница, чийто код се тегли и изпълнява чак при първото ѝ отваряне
 * (docs/architecture.md §Код при отваряне). Вика се НА НИВО МОДУЛ
 * (`routes/lazyPages.ts`), никога в render: върнатият компонент трябва да е
 * един и същ при всяко рендериране — иначе React монтира страницата наново и
 * губи състоянието ѝ (избрания склад, пазача за незаписан документ).
 *
 * Suspense е ЕДИН, над `<Routes>` в `main.tsx`: рутерът сменя екраните като
 * transition и React задържа стария екран, докато кодът на новия е готов —
 * без празен кадър. Границата за грешки е ВЪТРЕ, под
 * `ProtectedRoute`/`AuthorizedRoute`: без право кодът изобщо не се тегли, а
 * ПИН екранът не зависи от него.
 *
 * Провалът остава запомнен в lazy-то: нов `import()` на същия файл в същия
 * прозорец така или иначе връща същата грешка (`lib/chunkLoadRecovery.ts`).
 */
export function lazyPage<Props extends object>(
  load: () => Promise<{ default: ComponentType<Props> }>,
  placement: PagePlacement = "screen",
): ComponentType<Props> {
  // Броим само тегленето: чакането на закриване преди тихото презареждане не
  // бива да държи spinner-а над екрана, на който човекът вече е отишъл.
  const LoadedPage = lazy(() => importWithRecovery(() => trackPageLoad(load())));

  function LazyPage(props: Props) {
    return (
      <LazyPageErrorBoundary placement={placement}>
        <LoadedPage {...props} />
      </LazyPageErrorBoundary>
    );
  }
  return LazyPage;
}
